import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock } from "@phosphor-icons/react";
import { useFetch } from "@/lib/api";
import { catImg } from "@/pages/ContentHub";
import { Seo, articleJsonLd } from "@/components/Seo";
import { FundLinesChart } from "@/components/FundLinesChart";
import { WeeklyEmailToggle } from "@/components/WeeklyEmailToggle";
import { AuthorBox } from "@/components/AuthorBox";

export default function Article() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/articles/${slug}`);
  if (loading) return <p className="text-sm text-muted-foreground">Loading article...</p>;
  if (error) return <div data-testid="article-not-found"><p className="text-sm">Article not found.</p><Link to="/learn" className="text-cyan-700 underline text-sm">Back to Content Hub</Link></div>;
  return (
    <article className="max-w-3xl fade-up" data-testid="article-page">
      <Seo title={data.title} description={data.meta_description || data.excerpt} path={`/learn/${data.slug}`} type="article" jsonLd={articleJsonLd(data, `/learn/${data.slug}`)} />
      <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-cyan-700 dark:text-cyan-400 mb-6" data-testid="article-back-link"><ArrowLeft size={14} /> Content Hub</Link>
      <Link to={`/learn?category=${encodeURIComponent(data.category)}`} className="block text-xs uppercase tracking-wider text-cyan-700 dark:text-cyan-400">{data.category}</Link>
      <h1 className="text-4xl sm:text-5xl font-semibold mt-3 leading-tight text-grad-head" data-testid="article-title">{data.title}</h1>
      <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2"><Clock size={14} /> {data.read_time} min read · {data.published_date} · By <Link to="/editorial-policy" className="underline">PillarWealth Editorial Team</Link></p>
      <img src={catImg(data.category)} alt="" className="w-full h-64 object-cover rounded-lg mt-8" />
      {data.category === "Daily TSP Market Recap" && <FundLinesChart end={data.published_date} />}
      <div className="glass p-6 sm:p-10 mt-8 article-body text-slate-900 dark:text-slate-100" data-testid="article-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.body}</ReactMarkdown>
      </div>
      <div className="flex flex-wrap gap-2 mt-6">{data.tags.map((t) => <span key={t} className="text-xs rounded-full border border-border px-3 py-1 text-slate-600 dark:text-slate-400">#{t}</span>)}</div>
      <AuthorBox date={data.published_date} />
      {data.category === "Daily TSP Market Recap" && <div className="mt-8"><WeeklyEmailToggle compactCard /></div>}
      {data.related?.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-lg font-semibold mb-4">More in {data.category}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {data.related.map((r) => <Link key={r.slug} to={`/learn/${r.slug}`} className="glass glass-lift p-4 text-sm font-medium" data-testid={`related-${r.slug}`}>{r.title}</Link>)}
          </div>
        </div>
      )}
    </article>
  );
}
